import { HelixHttpRequestClient, helixHttpRequestClientKey } from '@/composables/http/use-http-request';
import { useHUpdateSettings } from '@/composables/use-update-settings';
import { settingsKey, watchApiSettings } from '@/composables/use-settings';
import { watchLegacyCustomerStrings } from '@/composables/use-update-translations';
import { IOnifyHelixConfig } from '@/shared/interfaces/IOnifyHelixConfig';
import { ISettings } from '@/shared/interfaces/ISettings';

import { App, AsyncComponentLoader, computed } from 'vue';
import VueDompurifyHTMLPlugin from 'vue-dompurify-html';
import { helixConfigKey } from '../composables/use-helix-config';
import { IThemePackProvider, themePackKey } from '../composables/use-theme-pack';
import { layoutsKey } from '@/composables/use-layouts';
import { DefaultsSymbol } from '../composables/use-defaults';

let _stopApiSettingsWatcher: (() => void) | null = null;
let _stopLegacyCustomerStringsWatcher: (() => void) | null = null;

export function useProvideHConfig(app: App, helixConfig: IOnifyHelixConfig) {
  app.provide(helixConfigKey, helixConfig);
}

export function useProvideHHttpRequestClient(app: App, helixHttpRequestClient: HelixHttpRequestClient) {
  app.provide(helixHttpRequestClientKey, helixHttpRequestClient);
}

export function useProvideHThemePack(app: App, themePackProvider: IThemePackProvider) {
  app.provide(themePackKey, themePackProvider);
}

export function useProvideHSettings(app: App, settings: ISettings) {
  app.provide(settingsKey, settings);
  _stopApiSettingsWatcher?.();
  _stopApiSettingsWatcher = watchApiSettings(settings);
  _stopLegacyCustomerStringsWatcher?.();
  _stopLegacyCustomerStringsWatcher = watchLegacyCustomerStrings();
}

export function useProvideHLayouts(app: App, layouts: Record<string, AsyncComponentLoader>) {
  app.provide(layoutsKey, layouts);
}

export function useProvideHDefaultsContext(app: App, themePackProvider: IThemePackProvider) {
  app.provide(DefaultsSymbol, {
    allDefaults: computed(() => themePackProvider.hThemePack.value?.vuetify?.defaults ?? {}),
    basePath: '',
  });
}

export function hInstallHelixCore(
  helixConfig: IOnifyHelixConfig,
  helixThemePackProvider: IThemePackProvider,
  helixSettings: ISettings,
  helixHttpRequestClient: HelixHttpRequestClient,
  helixLayouts: Record<string, AsyncComponentLoader> = {},
) {
  return {
    install(app: App) {
      /* 
        Provide HTTP client initialized/configured in `helix-app` so it may be used in `helix-pages` or other dependents
        Afterwards you can use `useHFetch(...)` to perform http/api requests
        See plugin code in ./plugins/http-request.js
      */
      useProvideHHttpRequestClient(app, helixHttpRequestClient);

      app.runWithContext(() => {
        useHUpdateSettings('my/config/settings', helixHttpRequestClient);
      });

      /*
        Provide Config to `helix-pages` and other packages\
        Afterwards you can use `useHConfig` to access values specified in `helixConfig`
      */
      useProvideHConfig(app, helixConfig);

      // Setup Theme Pack Provider
      useProvideHThemePack(app, helixThemePackProvider);

      // Setup Defaults Context for component defaults resolution
      useProvideHDefaultsContext(app, helixThemePackProvider);

      // Setup Helix Settings
      useProvideHSettings(app, helixSettings);

      // Setup Helix Layouts
      useProvideHLayouts(app, helixLayouts);

      app.use(VueDompurifyHTMLPlugin, {
        default: {
          // Preserve external-link behavior from config strings (target="_blank").
          // Default DOMPurify also keeps br, span, ul, li, and class — verified in test/unit/safe-html-dompurify.spec.js (STRING_HTML_AUDIT).
          ADD_ATTR: ['target'],
        },
      });
    },
  };
}
